import AddIcon from "@mui/icons-material/Add";
import CleaningServicesOutlined from "@mui/icons-material/CleaningServicesOutlined";
import FilterAltOffOutlinedIcon from "@mui/icons-material/FilterAltOffOutlined";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import {
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { BackdropCustom } from "../../../components/backdrop/Backdrop";
import { PageCard } from "../../../components/pageCard/PageCard";
import { CustomMenuItem } from "../../../components/table/CustomMenuItem/CustomMenuItem";
import { CustomNoContentTableBody } from "../../../components/table/CustomNoContentTableBody/CustomNoContentTableBody";
import { CustomTableCell } from "../../../components/table/CustomTableCell/CustomTableCell";
import CustomTablePagination from "../../../components/table/CustomTablePagination/CustomTablePagination";
import CustomTableSortLabel from "../../../components/table/CustomTableSortLabel/CustomTableSortLabel";
import { FilterButtonGroup } from "../../../components/table/FilterButtonGroup/FilterButtonGroup";
import { GridFilterArea } from "../../../components/table/GridFilterArea/GridFilterArea";
import { TitleContainer } from "../../../components/titleContainer/TitleContainer";
import { useToastr } from "../../../components/toast/Toast";
import { IJudgmentCriteria } from "../../../interfaces/IJudgmentCriteria";
import api from "../../../services/api";

const fields = [
  {
    field: "criteriaName",
    label: "Critério",
  },
];

const STORAGE_KEY = "@ORQUIDEAS:judgmentCriteriaFilters";

const getInitialState = (key: string, defaultValue: any) => {
  const saved = localStorage.getItem(STORAGE_KEY);
  if (saved) {
    const parsed = JSON.parse(saved);
    return parsed[key] !== undefined ? parsed[key] : defaultValue;
  }
  return defaultValue;
};

interface IJudgmentCriteriaListProps {
  judgmentId: string;
  judgmentTypeId?: number;
  registrationDisabled?: boolean;
}

interface IParams {
  perPage: number;
  currentPage: number;
  orderField: string;
  orderDirection: "asc" | "desc";
  field?: string;
  value?: string;
  precision?: string;
  delay?: number;
}

export const JudgmentCriteriaList: React.FC<IJudgmentCriteriaListProps> = ({
  judgmentId,
  judgmentTypeId,
  registrationDisabled,
}) => {
  const toastr = useToastr();
  const timeout = useRef<any>(null);
  const navigate = useNavigate();

  const [judgmentCriteriaItems, setJudgmentCriteriaItems] = useState<
    IJudgmentCriteria[]
  >([]);

  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState<number>(() =>
    getInitialState("perPage", 10),
  );
  const [currentPage, setCurrentPage] = useState<number>(() =>
    getInitialState("currentPage", 0),
  );
  const [orderDirection, setOrderDirection] = useState<"asc" | "desc">(() =>
    getInitialState("orderDirection", "asc"),
  );
  const [orderField, setOrderField] = useState<string>(() =>
    getInitialState("orderField", "sequence"),
  );
  const [loading, setLoading] = useState(false);

  const [gridFilterAreaOpen, setGridFilterAreaOpen] = useState(() =>
    getInitialState("gridFilterAreaOpen", false),
  );
  const [filterField, setFilterField] = useState<string>(() =>
    getInitialState("filterField", ""),
  );
  const [filterPrecision, setFilterPrecision] = useState<string>(() =>
    getInitialState("filterPrecision", "equal"),
  );
  const [filterValue, setFilterValue] = useState<string>(() =>
    getInitialState("filterValue", ""),
  );
  const [delay, setDelay] = useState(0);

  const handleToJudgmentCriteria = useCallback(
    (judgmentCriteriaId?: string) => {
      if (judgmentCriteriaId) {
        navigate(
          `/judgment-criteria/${judgmentCriteriaId}?judgmentId=${judgmentId}&judgmentTypeId=${judgmentTypeId}`,
        );
      } else {
        navigate(
          `/judgment-criteria?judgmentId=${judgmentId}&judgmentTypeId=${judgmentTypeId}`,
        );
      }
    },
    [judgmentId, judgmentTypeId, navigate],
  );

  const handlePagination = async (
    perPage: number,
    currentPage: number,
    orderField: string,
    orderDirection: "asc" | "desc",
  ) => {
    handleListAll({
      perPage,
      currentPage,
      orderField,
      orderDirection,
      field: filterField,
      value: filterValue,
      precision: filterPrecision,
      delay: 0,
    });
  };

  const handleListAll = useCallback(
    async ({
      perPage,
      currentPage,
      orderField,
      orderDirection,
      field,
      value,
      precision,
      delay,
    }: IParams) => {
      let url = `/judgment-criteria/all/${judgmentId}?perPage=${perPage}&currentPage=${
        currentPage + 1
      }&orderBy=${orderField}&orderDirection=${orderDirection}`;

      if (field && value && precision) {
        url =
          url +
          `&filterField=${field}&filterValue=${value}&precision=${precision}`;
      }

      clearInterval(timeout.current);
      timeout.current = setTimeout(async () => {
        setLoading(true);
        await api
          .get(url)
          .then(response => {
            if (response?.data?.data.length > 0) {
              setJudgmentCriteriaItems(response.data.data);
              setTotalRows(response.data.totalRows);
              setLoading(false);
            } else {
              setJudgmentCriteriaItems([]);
              setTotalRows(0);
              setPerPage(10);
              setCurrentPage(0);
              setLoading(false);
            }
          })
          .catch(error => {
            toastr.error(error?.message || "Contate a equipe de suporte");
          })
          .finally(() => {
            setLoading(false);
          });
      }, delay || 0);
    },
    [
      judgmentId,
      toastr,
      timeout,
      setLoading,
      setJudgmentCriteriaItems,
      setTotalRows,
      setPerPage,
      setCurrentPage,
    ],
  );

  const handleDeleteJudgmentCriteria = useCallback(
    async (judgmentCriteriaId: string) => {
      await api
        .delete(`/judgment-criteria/${judgmentCriteriaId}`)
        .then(() => {
          toastr.success("Critério de julgamento excluído com sucesso");
          handleListAll({
            perPage,
            currentPage,
            orderField,
            orderDirection,
            field: filterField,
            value: filterValue,
            precision: filterPrecision,
            delay: 0,
          });
        })
        .catch(error => {
          toastr.error(error?.message || "Contate a equipe de suporte");
        });
    },
    [
      currentPage,
      filterField,
      filterPrecision,
      filterValue,
      handleListAll,
      orderDirection,
      orderField,
      perPage,
      toastr,
    ],
  );

  const saveFiltersToStorage = useCallback(() => {
    const filters = {
      filterField,
      filterPrecision,
      filterValue,
      gridFilterAreaOpen,
      currentPage,
      perPage,
      orderField,
      orderDirection,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
  }, [
    filterField,
    filterPrecision,
    filterValue,
    gridFilterAreaOpen,
    currentPage,
    perPage,
    orderField,
    orderDirection,
  ]);

  useEffect(() => {
    saveFiltersToStorage();
  }, [
    filterField,
    filterPrecision,
    filterValue,
    gridFilterAreaOpen,
    currentPage,
    perPage,
    saveFiltersToStorage,
  ]);

  useEffect(() => {
    handlePagination(perPage, 0, orderField, "asc");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [perPage, orderField]);

  useEffect(() => {
    const field = filterField;
    const value = filterValue;
    const precision = filterPrecision;

    handleListAll({
      perPage,
      currentPage,
      orderField,
      orderDirection,
      field,
      value,
      precision,
      delay,
    });
  }, [
    filterField,
    filterPrecision,
    filterValue,
    delay,
    currentPage,
    orderDirection,
    orderField,
    perPage,
    handleListAll,
  ]);

  const handleTextFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilterValue(e.target.value);
    setTotalRows(0);
    setCurrentPage(0);
    setDelay(500);
  };

  const handleClearFilters = () => {
    setFilterField("");
    setFilterPrecision("equal");
    setFilterValue("");
    setCurrentPage(0);
    setPerPage(10);
    setOrderField("sequence");
    setOrderDirection("asc");
    localStorage.removeItem(STORAGE_KEY);
  };

  return (
    <>
      <TitleContainer>Critérios de Julgamento</TitleContainer>
      <PageCard>
        <FilterButtonGroup>
          <div>
            <Tooltip title="Filtros" placement="top">
              <IconButton
                onClick={() => setGridFilterAreaOpen(!gridFilterAreaOpen)}
                size="small"
                color="primary"
                style={{ alignItems: "flex-end" }}
              >
                {gridFilterAreaOpen ? (
                  <FilterAltOffOutlinedIcon />
                ) : (
                  <FilterAltOutlinedIcon />
                )}
              </IconButton>
            </Tooltip>
            {gridFilterAreaOpen && (
              <Tooltip title="Limpar Filtros" placement="top">
                <IconButton
                  onClick={() => handleClearFilters()}
                  size="small"
                  color="primary"
                  style={{ alignItems: "flex-end" }}
                >
                  <CleaningServicesOutlined />
                </IconButton>
              </Tooltip>
            )}
          </div>

          <Tooltip title="Adicionar critério de julgamento" placement="top">
            <IconButton
              onClick={() => {
                handleToJudgmentCriteria();
              }}
              size="small"
              color="primary"
              style={{ alignItems: "flex-end" }}
              disabled={registrationDisabled}
            >
              <AddIcon />
            </IconButton>
          </Tooltip>
        </FilterButtonGroup>

        {gridFilterAreaOpen && (
          <GridFilterArea container spacing={2}>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel id="select-filter">Filtrar por</InputLabel>
                <Select
                  label="Filtrar Por"
                  value={`${filterField}`}
                  onChange={e => {
                    setFilterField(`${e.target.value}`);
                  }}
                >
                  {fields.map(filter => {
                    return (
                      <MenuItem key={filter.field} value={filter.field}>
                        {filter.label}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Precisão</InputLabel>
                <Select
                  label="Precisão"
                  value={`${filterPrecision}`}
                  onChange={e => {
                    setFilterPrecision(`${e.target.value}`);
                  }}
                >
                  <MenuItem key={"equal"} value={"equal"}>
                    Igual
                  </MenuItem>
                  <MenuItem key={"containing"} value={"containing"}>
                    Contendo
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={7}>
              <TextField
                fullWidth
                label="Valor a filtrar"
                placeholder="Valor a filtrar"
                value={filterValue}
                onChange={handleTextFilterChange}
                size="small"
              />
            </Grid>
          </GridFilterArea>
        )}

        <TableContainer
          sx={{
            minHeight: "250px",
          }}
        >
          <Table
            sx={{
              padding: "16px",
            }}
          >
            <TableHead>
              <TableRow
                sx={{
                  "&:last-child td, &:last-child th": {
                    borderBottom: 1,
                    borderColor: "#E5E5E5",
                  },
                }}
              >
                <CustomTableCell size="small" width={60}>
                  Ações
                </CustomTableCell>

                <CustomTableSortLabel
                  perPage={perPage}
                  currentPage={currentPage}
                  orderField={orderField}
                  field="sequence"
                  label="Sequência"
                  handleRequest={handlePagination}
                  orderDirection={orderDirection}
                  setField={setOrderField}
                  setDirection={setOrderDirection}
                />

                <CustomTableSortLabel
                  perPage={perPage}
                  currentPage={currentPage}
                  orderField={orderField}
                  field="criteriaName"
                  label="Critério"
                  handleRequest={handlePagination}
                  orderDirection={orderDirection}
                  setField={setOrderField}
                  setDirection={setOrderDirection}
                />
                <CustomTableSortLabel
                  perPage={perPage}
                  currentPage={currentPage}
                  orderField={orderField}
                  field="maximum"
                  label="Nota máxima"
                  handleRequest={handlePagination}
                  orderDirection={orderDirection}
                  setField={setOrderField}
                  setDirection={setOrderDirection}
                />
              </TableRow>
            </TableHead>
            <TableBody>
              {judgmentCriteriaItems.length > 0 &&
                judgmentCriteriaItems.map(judgmentCriteriaItem => {
                  return (
                    <TableRow
                      key={judgmentCriteriaItem.id}
                      sx={{
                        "&:last-child td, &:last-child th": {
                          border: 0,
                        },
                      }}
                    >
                      <CustomMenuItem
                        id={
                          judgmentCriteriaItem.id ? judgmentCriteriaItem.id : ""
                        }
                        handleToEdit={handleToJudgmentCriteria}
                        handleToDelete={handleDeleteJudgmentCriteria}
                        disableButton={registrationDisabled}
                      />

                      <CustomTableCell size="small" width={200}>
                        {judgmentCriteriaItem.criteria?.sequence}
                      </CustomTableCell>

                      <CustomTableCell size="small" width={200}>
                        {judgmentCriteriaItem.criteria?.name}
                      </CustomTableCell>
                      <CustomTableCell size="small" width={150}>
                        {judgmentCriteriaItem.maximum}
                      </CustomTableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>

          {judgmentCriteriaItems.length === 0 && (
            <CustomNoContentTableBody description="Nenhum critério encontrado" />
          )}
        </TableContainer>

        <CustomTablePagination
          totalRows={totalRows}
          currentPage={currentPage}
          perPage={perPage}
          handleRequest={handlePagination}
          setPerPage={setPerPage}
          setCurrentPage={setCurrentPage}
          orderField={orderField}
          orderDirection={orderDirection}
        />

        {loading && <BackdropCustom />}
      </PageCard>
    </>
  );
};
